import * as React from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { navigation, type AdminRole } from "@/lib/dashboard-data";
import { cn } from "@/lib/utils";

type TeamAccount = {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
};

const team: TeamAccount[] = [
  { id: "ADM-001", name: "Eddy Admin", email: "eddy@deuxda.com", role: "Super Admin" },
  { id: "ADM-002", name: "Amina K.", email: "amina.k@deuxda.com", role: "Support" },
  { id: "ADM-003", name: "Juma R.", email: "juma.r@deuxda.com", role: "Finance" },
];

const defaultAccess: Record<AdminRole, string[]> = {
  "Super Admin": navigation.map((item) => item.key),
  Admin: ["shops", "products", "users", "orders", "payments", "support", "help-center"],
  Operations: ["orders", "checks", "vehicle-inspection", "customer-data"],
  Support: ["support", "messages", "help-center", "notifications"],
  Finance: ["payments", "orders", "store-layout"],
  Moderator: ["notice", "moderation", "products"],
};

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? "A") + (parts.length > 1 ? parts[parts.length - 1]![0]! : "");
}

export function RolesPage() {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [access, setAccess] = React.useState<Record<string, string[]>>(() =>
    Object.fromEntries(team.map((member) => [member.id, defaultAccess[member.role]])),
  );
  const selected = team.find((member) => member.id === selectedId) ?? null;
  const selectedAccess = selected ? access[selected.id] ?? [] : [];

  const toggle = (key: string) => {
    if (!selected) return;
    setAccess((current) => {
      const existing = current[selected.id] ?? [];
      const next = existing.includes(key) ? existing.filter((item) => item !== key) : [...existing, key];
      return { ...current, [selected.id]: next };
    });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,5fr)]">
      <Card>
        <CardHeader className="px-5 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Administrative team</p>
          <p className="text-sm text-muted-foreground">{team.length} privileged accounts</p>
        </CardHeader>
        <CardContent className="p-2">
          {team.map((member) => (
            <button
              key={member.id}
              type="button"
              onClick={() => setSelectedId(member.id)}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors",
                selectedId === member.id ? "bg-accent" : "hover:bg-muted/60",
              )}
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent text-xs font-semibold uppercase text-accent-foreground">
                {initials(member.name)}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-foreground">{member.name}</span>
                <span className="block truncate text-xs text-muted-foreground">{member.email} · {member.role}</span>
              </span>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Roles and authorizations</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Each administrator only sees the spaces necessary for their work.</p>
          </div>
          <Button
            type="button"
            disabled={!selected}
            onClick={() => toast.success(`Authorizations saved for ${selected?.name}`)}
          >
            Save
          </Button>
        </CardHeader>
        <CardContent className="px-5 pb-5">
          {!selected ? (
            <p className="text-sm text-muted-foreground">Select an account.</p>
          ) : (
            <div className="space-y-1">
              <p className="pb-2 text-sm font-medium text-foreground">
                {selected.name} <span className="font-normal text-muted-foreground">· {selected.role}</span>
              </p>
              <div className="grid gap-1 sm:grid-cols-2">
                {navigation.map((item) => {
                  const checked = selectedAccess.includes(item.key);
                  return (
                    <label
                      key={item.key}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors",
                        checked ? "border-primary/30 bg-accent" : "border-border/60 bg-card hover:bg-muted/50",
                      )}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggle(item.key)} aria-label={item.title} />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground">{item.title}</span>
                        <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
